import React, { useState, useEffect } from "react";
import {
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  PlayIcon,
  EnvelopeIcon,
  ClockIcon,
  ChevronLeftIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { Grid2x2 } from "lucide-react";
import FAQModal from "../../../components/modal/FAQModal";
import { adminApi } from "../../../repositories/admin";
import { useAppSelector } from "../../../hooks/redux";
import { Skeleton, SkeletonText } from "../../ui/skeleton";
import { useTourContext } from "../../../context/TourContext";
import { useNavigate } from "react-router-dom";
import { tourApi } from "../../../repositories/tour";
import SettingsPanel from "../../ui/SettingsPanel";
import Button from "../../ui/Button";

interface AppUpdate {
  id: number;
  title: string;
  updateData: string;
  views: number;
  status: "published" | "draft";
  createdAt: string;
  updatedAt: string;
}

const Support = () => {
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [appUpdates, setAppUpdates] = useState<AppUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<AppUpdate | null>(null);
  const { user } = useAppSelector((state) => state.auth);
  const { clearTourProgress, setCurrentTourSection, markTourNotCompleted } = useTourContext();
  const navigate = useNavigate();


  useEffect(() => {
    fetchAppUpdates();
  }, []);

  const fetchAppUpdates = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getAllAppUpdates({
        page: 1,
        limit: 100,
        userRole: user?.role || "",
        includeDismissed: true,
        addPagination: false,
      });
      if (response?.data?.success) {
        const publishedUpdates = (response.data.data || []).filter(
          (update: AppUpdate) => update.status === "published"
        );
        setAppUpdates(publishedUpdates);
      }
    } catch (error) {
      console.error("Failed to fetch app updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClick = async (update: AppUpdate) => {
    try {
      if (user?.id) {
        await adminApi.viewAppUpdate(update.id, user.id);
      }
      setSelectedUpdate(update);
    } catch (error) {
      console.error("Failed to track update view:", error);
      setSelectedUpdate(update);
    }
  };

  const handleBackToList = () => {
    setSelectedUpdate(null);
  };

  const handleEmailSupport = () => {
    const email = 'admin@cadenceio.com';
    const subject = 'Support Request - CadenceIO';
    const body = 'Please describe your issue or question below:\n\n';
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    try {
      window.location.href = mailtoLink;
    } catch (error) {
      // Fallback for environments where mailto might not work
      navigator.clipboard.writeText(email).then(() => {
        Swal.fire({
          title: "Email Copied",
          text: `Email address ${email} has been copied to your clipboard.`,
          icon: "success",
          confirmButtonText: "OK",
        });
      }).catch(() => {
        Swal.fire({
          title: "Contact Support",
          text: `Please email us at: ${email}`,
          icon: "info",
          confirmButtonText: "OK",
        });
      });
    }
  };

  const handleStartTour = async () => {
    if (!user?.id) {
      Swal.fire({
        title: "Error",
        text: "Unable to start tour. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      clearTourProgress();

      // Only call tour APIs for basic users
      if (user?.role === "basic") {
        try {
          await tourApi.updateTourLog(user.id, { is_completed: false });
        } catch (error: any) {
          if (error?.response?.status === 404) {
            await tourApi.createTourLog(user.id, { is_completed: false });
          } else {
            throw error;
          }
        }
      }

      markTourNotCompleted();

      setCurrentTourSection("dashboard");

      navigate("/");
    } catch (error) {
      console.error("Error starting tour:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to start tour. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (

    <>
    <SettingsPanel>
      {selectedUpdate ? (
        // Full view for update detail
        <>
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 mb-4 text-sm text-primary-700 transition-colors hover:text-primary-800"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Back to updates
          </button>
          <h2 className="mb-4 text-lg font-semibold text-black sm:text-xl">
            {selectedUpdate.title}
          </h2>
          <hr className="mb-6 border-neutral-100" />
          <div
            className="prose-sm prose max-w-none sm:prose-base"
            dangerouslySetInnerHTML={{ __html: selectedUpdate.updateData }}
          />
        </>
      ) : (
        <>
          <h2 className="mb-4 text-base font-semibold text-black sm:text-lg">
            Support & Help
          </h2>
          <hr className="mb-4 border-neutral-100 sm:mb-6" />

          {/* Help Resources */}
          <div className="mb-4 sm:mb-6">
        <h3 className="mb-4 text-sm font-semibold text-black sm:text-base">
          Help Resources
        </h3>
        <div className="space-y-4">
          {/* Frequently Asked Questions */}
          <button
           onClick={() => setShowFAQModal(true)}
            className="flex items-center justify-between w-full p-3 transition-colors duration-200 border border-neutral-200 rounded-md sm:p-4 hover:bg-primary-50"
            aria-label="Open Frequently Asked Questions"
          >
            <div className="flex items-center">
              <QuestionMarkCircleIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-neutral-800 sm:text-sm">
                Frequently Asked Questions
              </span>
            </div>
            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5">
              <Grid2x2 className="w-4 h-4 text-neutral-400" />
            </div>
          </button>

          {/* Product Documentation */}
          <a
            href={process.env.REACT_APP_PRODUCT_DOCS_URL}
            target="_blank"
            className="flex items-center justify-between w-full p-3 transition-colors duration-200 border border-neutral-200 rounded-md cursor-pointer sm:p-4 hover:bg-primary-50"
            aria-label="Open Product Documentation"
          >
            <div className="flex items-center">
              <DocumentTextIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-neutral-800 sm:text-sm">
                Product Documentation
              </span>
            </div>
            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5">
              <Grid2x2 className="w-4 h-4 text-neutral-400" />
            </div>
          </a>

          {/* Watch Demo Video */}
          <a
            href={process.env.REACT_APP_DEMO_VIDEO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between w-full p-3 transition-colors duration-200 border border-neutral-200 rounded-md sm:p-4 hover:bg-primary-50"
            aria-label="Watch Demo Video"
          >
            <div className="flex items-center">
              <PlayIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-neutral-800 sm:text-sm">
                Watch Demo Video
              </span>
            </div>
            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5">
              <Grid2x2 className="w-4 h-4 text-neutral-400" />
            </div>
          </a>

          {/* Start Product Tour */}
          <button
            onClick={handleStartTour}
            className="flex items-center justify-between w-full p-3 transition-colors duration-200 border border-neutral-200 rounded-md sm:p-4 hover:bg-primary-50"
            aria-label="Start Product Tour"
          >
            <div className="flex items-center">
              <AcademicCapIcon className="w-4 h-4 mr-2 text-neutral-600 sm:w-5 sm:h-5" />
              <span className="text-xs text-neutral-800 sm:text-sm">
                Start Product Tour
              </span>
            </div>
            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5">
              <Grid2x2 className="w-4 h-4 text-neutral-400" />
            </div>
          </button>
        </div>
      </div>

      {/* Contact Support */}
      <div className="mb-4 sm:mb-6">
        <h3 className="mb-4 text-sm font-semibold text-black sm:text-base">
          Contact Support
        </h3>
        <div className="space-y-4">
          {/* Email Support Team */}
          <div className="flex flex-row items-start justify-between p-3 border rounded-md border-neutral-200 max-sm:flex-col sm:p-4">
            <div className="mb-2 sm:mb-0">
              <p className="text-xs font-medium text-neutral-800 sm:text-sm">
                Email Support Team
              </p>
              <p className="text-xs text-neutral-600">admin@cadenceio.com</p>
            </div>
            <Button
              size="sm"
              onClick={handleEmailSupport}
              className="w-auto"
            >
              <EnvelopeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>

          {/* Schedule a Call */}
          <div className="flex flex-row items-start justify-between p-3 border rounded-md border-neutral-200 max-sm:flex-col sm:p-4">
            <div className="mb-2 sm:mb-0">
              <p className="text-xs font-medium text-neutral-800 sm:text-sm">
                Schedule a Call
              </p>
              <p className="text-xs text-neutral-600">
                Book a 15-minute slot with our team
              </p>
            </div>
            <a
              href={process.env.REACT_APP_SCHEDULE_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-transparent inline-flex items-center justify-center bg-primary-700 text-white px-3 py-1.5 rounded-md hover:bg-primary-800 transition-colors w-auto focus:outline-none focus-visible:border-primary-950"
            >
              <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </a>
          </div>
        </div>
      </div>

          {/* What's New Section */}
          <div className="mb-4 sm:mb-6">
            <h3 className="mb-4 text-sm font-semibold text-black sm:text-base">
              What's New
            </h3>
            {loading ? (
              // Shaped like the update entries below, so the section keeps its
              // height instead of collapsing to a centred spinner.
              <div className="space-y-5" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="w-48 h-4" />
                    <SkeletonText lines={2} />
                  </div>
                ))}
              </div>
            ) : (
              // Updates List View
              <div>
                {appUpdates.length > 0 ? (
                  appUpdates.map((update, index) => (
                    <div key={update.id}>
                      <button
                        onClick={() => handleUpdateClick(update)}
                        className="border border-transparent flex items-start justify-between w-full py-4 text-left transition-colors rounded hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
                      >
                        <div className="flex items-start flex-1 gap-3">
                          <span className="flex-shrink-0 text-xs font-medium text-neutral-400 sm:text-sm">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="flex-1">
                            <h4 className="text-xs font-medium text-black sm:text-sm hover:text-primary-700">
                              {update.title}
                            </h4>
                            <p className="mt-1 text-xs text-neutral-500">
                              {new Date(update.updatedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>
                      </button>
                      {index < appUpdates.length - 1 && (
                        <hr className="border-neutral-100" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="py-6 text-center text-neutral-500">
                    <p className="text-xs sm:text-sm">No updates available at the moment.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </SettingsPanel>
    {/* FAQ Modal */}
      <FAQModal
        isOpen={showFAQModal}
        onClose={() => setShowFAQModal(false)}
      />

      </>
  );
};

export default Support;
